// DS: C35
import React from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import PremiumGallery from '@wix/da-ds/pkg/Icons/24x24/PremiumGallery';
import EyeDisc2 from '@wix/da-ds/pkg/Icons/16x16/EyeDisc2';
import SimpleTooltip from '@wix/da-ds/pkg/tooltips/dsPreset/SimpleTooltip';
import UserAvatar from '../../User/Avatar';
import { getIsStash } from '../../types/deviation/getters';
import { normalizePremiumFolderData } from '../../utils/normalizePremiumFolderData';
import { getDeviationTitle } from '../../DeviationViews/_helpers';
import Author from '../_partials/Author';
import GroupDetail from '../_partials/GroupDetail';
import Title from '../_partials/Title';
import PublishedTime from '../_partials/PublishedTime';
import AdoptableOwner from '../_partials/AdoptableOwner';

import s from './DesktopSubmissionDetails.scss';

export interface Props {
  deviation: PapiDeviation;
  isOwner: boolean;
  showTitle?: boolean;
  className?: string;
}

const DesktopSubmissionDetails: React.FC<React.PropsWithChildren<Props>> = ({
  className,
  deviation,
  deviation: { author, extended },
  isOwner,
  showTitle,
}) => {
  const { t } = useTranslation();
  const deviationTitle = getDeviationTitle(deviation);
  const isShowingTitle = deviationTitle && showTitle;
  const { isPremium, isPaidFolder } = normalizePremiumFolderData(
    deviation?.premiumFolderData
  );

  const premiumTooltip = isPaidFolder
    ? t('deviation.details.premiumFolder.tooltip')
    : t('deviation.details.watchersOnlyFolder.tooltip');
  const PremiumIcon = isPaidFolder ? PremiumGallery : EyeDisc2;

  const adoptable = deviation.extended?.adoptable;

  return (
    <div
      className={classnames(
        s['root'],
        !isShowingTitle && s['no-title'],
        className
      )}
    >
      <BiLoggerContextProvider value={{ sectionname: 'author' }}>
        <div className={s['left']}>
          {author && (
            <UserAvatar
              withTooltip
              relatedItemBiData={{
                typeid: deviation.typeId,
                itemid: deviation.deviationId,
              }}
              size={50}
              user={extended?.exclusiveGroup ?? author}
              className={s['avatar']}
            />
          )}
        </div>

        <div className={s['middle']}>
          {isShowingTitle && (
            <div className={s['title-row']}>
              {isPremium && (
                <SimpleTooltip
                  className={s['premium-icon']}
                  text={premiumTooltip}
                >
                  <PremiumIcon size={IconSize.SMALL} />
                </SimpleTooltip>
              )}

              <Title
                title={deviationTitle}
                showTitle={true}
                className={s['title']}
              />
            </div>
          )}

          <div className={s['middle-lower']}>
            {extended?.exclusiveGroup && (
              <GroupDetail
                group={extended.exclusiveGroup}
                className={s['group']}
              />
            )}

            <Author
              author={extended?.originalAuthor ?? author}
              isOwner={isOwner}
              className={s['original-author']}
            />
          </div>
        </div>

        <div className={s['right']}>
          <PublishedTime
            isStash={getIsStash(deviation)}
            className={s['published-time']}
            publishedTime={deviation.publishedTime}
          />
          {adoptable && (
            <AdoptableOwner
              className={s['adoptable-owner']}
              adoptable={adoptable}
            />
          )}
        </div>
      </BiLoggerContextProvider>
    </div>
  );
};
DesktopSubmissionDetails.displayName = 'DesktopSubmissionDetails';

export default DesktopSubmissionDetails;
